import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

const WHEEL_TIMEOUT = 333;

export const useWheelPassthrough = (viewportRef: RefObject<HTMLElement | null>, elementRef: RefObject<HTMLElement | null>) => {
  const wheelTimeoutRef = useRef(0);
  const [isPassingThrough, setIsPassingThrough] = useState(false);

  const onWheelScroll = useCallback(
    (event: WheelEvent) => {
      if (!viewportRef.current) {
        return;
      }

      const viewport = viewportRef.current;

      const scrollPos = { left: viewport.scrollLeft + event.deltaX, top: viewport.scrollTop + event.deltaY };
      if (!isPassingThrough && event.deltaMode === 0) {
        viewport.scrollTo({ ...scrollPos, behavior: "smooth" });
      }

      setIsPassingThrough(true);

      if (wheelTimeoutRef.current) {
        window.clearTimeout(wheelTimeoutRef.current);
      }
      wheelTimeoutRef.current = window.setTimeout(() => {
        setIsPassingThrough(false);
      }, WHEEL_TIMEOUT);

      event.preventDefault();
    },
    [viewportRef, isPassingThrough],
  );

  useEffect(() => {
    if (!elementRef) {
      return;
    }

    const element = elementRef.current;

    const onWheel = (event: WheelEvent) => {
      if (element?.contains(event.target as HTMLElement)) {
        onWheelScroll(event);
      }
    };

    document.addEventListener("wheel", onWheel, { passive: false });

    return () => document.removeEventListener("wheel", onWheel, { passive: false } as EventListenerOptions);
  }, [onWheelScroll, elementRef]);

  return isPassingThrough;
};
